import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import Connections from "@/components/settings/Connections";

function BrandSettings({ canEdit }) {
  const { org, setOrg } = useAuth();
  const [brand, setBrand] = useState({
    name: org?.name || "", website: org?.website || "", industry: org?.industry || "",
    logo_url: org?.logo_url || "", primary_color: org?.primary_color || "#605CD2",
    secondary_color: org?.secondary_color || "#FF746D",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setBrand({ ...brand, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/org/brand", brand); setOrg(data); toast.success("Brand profile saved"); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl space-y-4 rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
      <div className="space-y-1.5"><Label>Brand name</Label><Input data-testid="set-name" value={brand.name} onChange={set("name")} disabled={!canEdit} /></div>
      <div className="space-y-1.5">
        <Label>Website URL</Label>
        <Input data-testid="set-website" value={brand.website} onChange={set("website")} disabled={!canEdit} placeholder="https://www.yourbrand.com" />
        <p className="text-xs text-muted-foreground">Used for brand identity & imagery. Website analytics come from your connected GA4 property.</p>
      </div>
      <div className="space-y-1.5"><Label>Logo URL</Label><Input data-testid="set-logo" value={brand.logo_url} onChange={set("logo_url")} disabled={!canEdit} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label>Primary colour</Label>
          <div className="flex gap-2"><input type="color" value={brand.primary_color} onChange={set("primary_color")} disabled={!canEdit} className="h-10 w-12 rounded-lg border border-border" /><Input value={brand.primary_color} onChange={set("primary_color")} disabled={!canEdit} /></div>
        </div>
        <div className="space-y-1.5"><Label>Secondary colour</Label>
          <div className="flex gap-2"><input type="color" value={brand.secondary_color} onChange={set("secondary_color")} disabled={!canEdit} className="h-10 w-12 rounded-lg border border-border" /><Input value={brand.secondary_color} onChange={set("secondary_color")} disabled={!canEdit} /></div>
        </div>
      </div>
      {canEdit && <Button data-testid="set-brand-save" onClick={save} disabled={saving} className="rounded-full bg-brand-primary text-brand-primary-foreground hover:opacity-90">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>}
    </div>
  );
}

function Team({ canEdit }) {
  const qc = useQueryClient();
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const [form, setForm] = useState({ name: "", email: "", role: "viewer", password: "" });
  const [open, setOpen] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const add = async () => {
    try { await api.post("/team", form); qc.invalidateQueries({ queryKey: ["team"] }); setOpen(false); setForm({ name: "", email: "", role: "viewer", password: "" }); toast.success("Member added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  return (
    <div className="max-w-2xl space-y-4">
      <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <p className="font-semibold">Team members</p>
          {canEdit && <Button size="sm" onClick={() => setOpen(!open)} className="rounded-full bg-brand-primary text-brand-primary-foreground" data-testid="team-add-toggle"><Plus className="mr-1 h-4 w-4" />Add</Button>}
        </div>
        {open && (
          <div className="mb-4 grid gap-3 rounded-xl border border-border/60 bg-muted/30 p-4 sm:grid-cols-2">
            <Input placeholder="Name" value={form.name} onChange={set("name")} data-testid="team-name" />
            <Input placeholder="Email" type="email" value={form.email} onChange={set("email")} data-testid="team-email" />
            <Input placeholder="Password" type="password" value={form.password} onChange={set("password")} data-testid="team-password" />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="team-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="viewer">Viewer</SelectItem></SelectContent>
            </Select>
            <Button onClick={add} className="rounded-full bg-brand-primary text-brand-primary-foreground sm:col-span-2" data-testid="team-save">Add member</Button>
          </div>
        )}
        <div className="space-y-2">
          {team?.map((m) => (
            <div key={m.id} className="flex items-center gap-3 rounded-xl border border-border/60 p-3">
              <div className="grid h-9 w-9 place-items-center rounded-full bg-brand-secondary/15 text-sm font-bold">{m.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1"><p className="text-sm font-semibold">{m.name}</p><p className="text-xs text-muted-foreground">{m.email}</p></div>
              <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-semibold capitalize">{m.role}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Settings</h1>
        <p className="mt-1 text-muted-foreground">Manage your connections, brand and team.</p>
      </header>
      <Tabs defaultValue="connections">
        <TabsList className="rounded-full">
          <TabsTrigger value="connections" data-testid="tab-connections" className="rounded-full">Connections</TabsTrigger>
          <TabsTrigger value="brand" data-testid="tab-brand" className="rounded-full">Brand Profile</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team" className="rounded-full">Team</TabsTrigger>
        </TabsList>
        <TabsContent value="connections" className="mt-6"><Connections /></TabsContent>
        <TabsContent value="brand" className="mt-6"><BrandSettings canEdit={isAdmin} /></TabsContent>
        <TabsContent value="team" className="mt-6"><Team canEdit={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}
